import { useEffect, useState } from 'react';
import { Box, Download, Loader2, Play, Wand2, Wrench, ImagePlus, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../common/components/PageContainer';
import { FeatureGate } from '../../../components/FeatureGate';
import { PlanMeters } from '../../../components/design-system/plan/PlanMeters';
import { usePlanUsage } from '../../../components/design-system/plan/usePlanUsage';
import { useProductTypesApi } from '../../../common/hooks/use-product-types-api.hook';
import * as _ from "lodash";
import { HerringbonePatternForm } from '../../../modules/vendor/seamless-generator/components/HerringbonePattern.form';
import { BricksPatternForm } from '../../../modules/vendor/seamless-generator/components/BricksPattern.form';
import { ChevronPatternForm } from '../../../modules/vendor/seamless-generator/components/ChevronPattern.form';
import { StackBondPatternForm } from '../../../modules/vendor/seamless-generator/components/StackBondPattern.form';
import { OpusPatternForm } from '../../../modules/vendor/seamless-generator/components/OpusPattern.form';
import { CabochonPatternForm } from '../../../modules/vendor/seamless-generator/components/CabochonPattern.form';
import { HexagonalPatternForm } from '../../../modules/vendor/seamless-generator/components/HexagonalPattern.form';
import { FishScalePatternForm } from '../../../modules/vendor/seamless-generator/components/FishScalePattern.form';
import { ArabesquePatternForm } from '../../../modules/vendor/seamless-generator/components/ArabesquePattern.form';
import { AlurePatternForm } from '../../../modules/vendor/seamless-generator/components/AlurePattern.form';
import { ArrowPatternForm } from '../../../modules/vendor/seamless-generator/components/ArrowPattern.form';
import { PennyPatternForm } from '../../../modules/vendor/seamless-generator/components/PennyPattern.form';
import { TrianglePatternForm } from '../../../modules/vendor/seamless-generator/components/TrianglePattern.form';
import { FlagstonePatternForm } from '../../../modules/vendor/seamless-generator/components/FlagstonePattern.form';
import { WavePatternForm } from '../../../modules/vendor/seamless-generator/components/WavePattern.form';
import { LongHexPatternForm } from '../../../modules/vendor/seamless-generator/components/LongHexPattern.form';
import { AlternatingPatternForm } from '../../../modules/vendor/seamless-generator/components/AlternatingPattern.form';
import { SeamlessResultDialog } from '../../../modules/vendor/seamless-generator/modals/seamless-result.modal';
import { useDropzone } from 'react-dropzone';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// Styled DropZone component for drag and drop image uploads
const DropZoneArea = ({ onDrop, multiple = true }) => {
  const { t } = useTranslation();

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    onDrop,
    multiple
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex min-h-[140px] cursor-pointer flex-col items-center justify-center border-2 border-dashed p-6 text-center transition-colors',
        isDragAccept
          ? 'border-success bg-success/5'
          : isDragReject
            ? 'border-destructive bg-destructive/5'
            : isDragActive
              ? 'border-foreground bg-accent'
              : 'border-border bg-muted/40 hover:border-foreground/40 hover:bg-muted'
      )}
    >
      <input {...getInputProps()} />
      <ImagePlus className="mb-4 size-10 text-muted-foreground" />
      <p className="mb-1 text-sm font-medium text-foreground">
        {isDragActive
          ? isDragAccept
            ? t('PATTERNS.PARTIALS.DRAG_DROP_PLANKS')
            : t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')
          : t('PATTERNS.PARTIALS.DRAG_DROP_PLANKS')}
      </p>
      <p className="text-xs text-muted-foreground">
        {t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
      </p>
    </div>
  );
};

// Image Preview component
const ImagePreview = ({ images, onRemove }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-5">
      <p className="mb-3 text-sm font-medium text-foreground">
        {t('PATTERNS.PARTIALS.IMAGES')} ({images.length})
      </p>

      <div className="flex flex-wrap gap-3">
        {images.map((img, index) => (
          <div
            key={index}
            className="relative size-[72px] border border-border transition-transform hover:scale-105"
          >
            <img
              src={typeof img === 'string' ? img : URL.createObjectURL(img)}
              alt={`Uploaded ${index}`}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="absolute -end-2 -top-2 flex size-6 items-center justify-center rounded-full border border-border bg-background text-base text-muted-foreground transition-colors hover:border-destructive hover:bg-destructive/5 hover:text-destructive"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {images.length > 0 && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRemove(-1)} // -1 means remove all
          className="mt-4 border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
        >
          {t('PATTERNS.PARTIALS.REPLACE_IMAGES')}
        </Button>
      )}
    </div>
  );
};

// Pattern Card component for the sidebar
const PatternCard = ({ pattern, isSelected, onClick, t }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'mb-3 flex w-full cursor-pointer flex-col items-center border p-3 transition-colors',
      isSelected
        ? 'border-foreground bg-muted'
        : 'border-border bg-card rounded-xl hover:border-foreground/40 hover:bg-muted/50'
    )}
  >
    <span
      className={cn(
        'mb-2 flex size-12 items-center justify-center rounded-full p-1',
        isSelected ? 'bg-background' : 'bg-muted'
      )}
    >
      <img
        src={pattern.icon}
        alt={pattern.name}
        className="size-8 object-contain dark:invert"
      />
    </span>
    <span
      className={cn(
        'text-center text-xs font-medium capitalize',
        isSelected ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      {t(`PATTERNS.${pattern.name.toUpperCase().replace(' ','_')}`)}
    </span>
  </button>
);

// Main component
export const SeamlessGenerator = () => {
  const { t } = useTranslation();
  const [patternList, setPatternList] = useState([]);
  const [seamlessResult, setSeamlessResult] = useState<any>();
  const [isSeamlessResultViewerOpen, setIsSeamlessResultViewerOpen] = useState<any>(false);
  const [selectedPattern, setSelectedPattern] = useState<any>();
  const [uploadedImages, setUploadedImages] = useState([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(true);

  // Handle file drop
  const onDrop = (acceptedFiles) => {
    setUploadedImages(prevImages => [...prevImages, ...acceptedFiles]);
  };

  // Remove uploaded image
  const removeImage = (index) => {
    if (index === -1) {
      setUploadedImages([]);
    } else {
      setUploadedImages(prevImages => prevImages.filter((_, i) => i !== index));
    }
  };

  const isSelected = (pattern) => {
    return pattern.name === selectedPattern?.name;
  };

  const productTypesApi = useProductTypesApi();

    useEffect(() => {
    const fetchPatterns = async () => {
      try {
        const types = await productTypesApi.findAllProductTypes({ activeOnly: true });
        const allPatterns = types.flatMap(pt => pt.patterns);
        setPatternList(_.uniqBy(allPatterns, 'name'));
      } catch (error) {
        console.error('Error fetching product types for patterns:', error);
      }
    };
    fetchPatterns();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    useEffect(() => {
    setIsSeamlessResultViewerOpen(false);
    setSeamlessResult(null);
  }, [selectedPattern]);

  // A texture made is one fewer this month: re-read the meter when one lands.
  const { refresh: refreshUsage } = usePlanUsage({ enabled: false });

    useEffect(() => {
        if(seamlessResult){
      setIsSeamlessResultViewerOpen(true);
      refreshUsage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seamlessResult]);

  // Mock function for generating on button click - in real implementation, this would connect to the actual generation process
  const handleGenerate = () => {
    if (!selectedPattern) return;

    // For patterns with their own upload handling, don't require uploadedImages
    const patternsWithOwnUploads = ['opus', 'cabochon'];
    const requiresMainUpload = !patternsWithOwnUploads.includes(selectedPattern.name);

    if (requiresMainUpload && uploadedImages.length === 0) return;

    setGenerateLoading(true);

    // Trigger the selected pattern's form generation function
    setTimeout(() => {
      switch(selectedPattern.name) {
        case "bricks":
          document.getElementById('bricks-generate-btn')?.click();
          break;
        case "herringbone":
          document.getElementById('herringbone-generate-btn')?.click();
          break;
        case "chevron":
          document.getElementById('chevron-generate-btn')?.click();
          break;
        case "stackbond":
          document.getElementById('stackbond-generate-btn')?.click();
          break;
        case "opus":
          document.getElementById('opus-generate-btn')?.click();
          break;
        case "cabochon":
          document.getElementById('cabochon-generate-btn')?.click();
          break;
        case "hexagonal":
          document.getElementById('hexagonal-generate-btn')?.click();
          break;
        case "fishscale":
        case "arabesque":
        case "alure":
        case "arrow":
        case "penny":
        case "triangle":
        case "wave":
        case "longhex":
        case "flagstone":
        case "flagstonetight":
        case "alternating":
          document.getElementById(`${selectedPattern.name}-generate-btn`)?.click();
          break;
        default:
          setGenerateLoading(false);
      }

      // In a real implementation, the loading would be handled by the actual API call
      setTimeout(() => {
        setGenerateLoading(false);

        // For patterns that use their own image handling logic
        const hasPatternSpecificImages = ['opus', 'cabochon'].includes(selectedPattern.name);

        // Check if we have images (either from main upload or from pattern form)
        if (hasPatternSpecificImages || uploadedImages.length > 0) {
          // For demo purposes - use first image as result or a placeholder
          const mockResultImage = hasPatternSpecificImages
            ? 'https://via.placeholder.com/800x800?text=Generated+Pattern'
            : URL.createObjectURL(uploadedImages[0]);

          setSeamlessResult({
            file: mockResultImage,
            success: true,
            message: 'Successfully generated'
          });

          // Open result dialog
          setIsSeamlessResultViewerOpen(true);
        }
      }, 1500);
    }, 500);
  };

  // Download generated seamless texture
  const handleDownload = () => {
    if (!seamlessResult) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${seamlessResult}`;
    link.download = `seamless-${selectedPattern?.name || 'texture'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

    return (
        <PageContainer breadcrumbs={[
            { label: t('SIDEBAR.VISUALIZER.TITLE') },
            { label: t('SIDEBAR.VISUALIZER.TOOLS'), icon: <Wrench className="size-5" /> },
            { label: t('SIDEBAR.VISUALIZER.SEAMLESS_GENERATOR') }
        ]}>
      <FeatureGate feature="seamlessGeneration">
      <div>
        <h1 className="font-heading mb-8 text-xl font-medium tracking-tight text-foreground">
          {t('SIDEBAR.VISUALIZER.SEAMLESS_GENERATOR')}
        </h1>

        {/* Seamless jobs left this month — only on a plan that meters them (Free: 3). */}
        <PlanMeters keys={['maxSeamlessPerMonth']} className="mb-6" testId="seamless-plan-meters" />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          {/* Pattern selection sidebar */}
          <div className="md:col-span-1">
            <Card className="flex h-[calc(100vh-220px)] max-h-[85vh] flex-col p-5">
              <p className="mb-5 text-sm font-medium text-foreground">
                {t('PATTERNS.PARTIALS.TITLE')}
              </p>

              <div className="flex-1 overflow-y-auto pt-1 pe-1">
                {patternList.map((pattern, index) => (
                  <PatternCard
                    key={index}
                    pattern={pattern}
                    isSelected={isSelected(pattern)}
                    onClick={() => setSelectedPattern(pattern)}
                    t={t}
                  />
                ))}
              </div>
            </Card>
          </div>

          {/* Main content area */}
          <div className="md:col-span-3">
            {selectedPattern ? (
              <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
                <Card className="mb-6 overflow-visible">
                  <CardContent className="p-6">
                    <div className="mb-6 flex items-center border-b pb-4">
                      <Box className="me-3 size-5 text-foreground" />
                      <h2 className="font-heading text-base font-medium tracking-tight text-foreground">
                        {t(`PATTERNS.${selectedPattern.name.toUpperCase().replace(' ','_')}`)}
                      </h2>
                    </div>

                    <div className="mb-6">
                      {/* Image upload section - Hidden for patterns with their own upload sections */}
                      {selectedPattern &&
                       !['opus', 'cabochon'].includes(selectedPattern.name) && (
                        <>
                          <p className="mb-3 text-sm font-medium text-foreground">
                            {t('PATTERNS.PARTIALS.IMAGES')}
                          </p>

                          <DropZoneArea onDrop={onDrop} />

                          {uploadedImages.length > 0 && (
                            <ImagePreview
                              images={uploadedImages}
                              onRemove={removeImage}
                            />
                          )}
                        </>
                      )}
                    </div>

                    {/* Pattern-specific form content */}
                    <div>
                      {selectedPattern.name === "bricks" && <BricksPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "herringbone" && <HerringbonePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "chevron" && <ChevronPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "stackbond" && <StackBondPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "opus" && <OpusPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} />}
                      {selectedPattern.name === "cabochon" && <CabochonPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} />}
                      {selectedPattern.name === "hexagonal" && <HexagonalPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} />}
                      {selectedPattern.name === "fishscale" && <FishScalePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "arabesque" && <ArabesquePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "alure" && <AlurePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "arrow" && <ArrowPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "penny" && <PennyPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "triangle" && <TrianglePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "flagstone" && <FlagstonePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "flagstonetight" && <FlagstonePatternForm tight seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "wave" && <WavePatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "longhex" && <LongHexPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                      {selectedPattern.name === "alternating" && <AlternatingPatternForm seamlessResult={seamlessResult} setSeamlessResult={setSeamlessResult} uploadedImages={uploadedImages}/>}
                    </div>
                  </CardContent>

                  <div className="mx-6 mt-2 mb-6 flex items-center justify-between border bg-muted/40 p-5">
                    <p className="text-sm text-muted-foreground">
                      {t('PATTERNS.PARTIALS.GENERATE_ALL_TEXTURES')}
                    </p>

                    <Button
                      variant="outline"
                      onClick={handleGenerate}
                      disabled={
                        generateLoading ||
                        (selectedPattern &&
                         !['opus', 'cabochon'].includes(selectedPattern.name) &&
                         uploadedImages.length === 0)
                      }
                    >
                      {generateLoading
                        ? <Loader2 data-icon="inline-start" className="animate-spin" />
                        : <Play data-icon="inline-start" />}
                      {generateLoading ? t('PATTERNS.PARTIALS.PROCESSING_TEXTURES') : t('PATTERNS.PARTIALS.GENERATE')}
                    </Button>
                  </div>
                </Card>

                {/* Result preview */}
                {seamlessResult && (
                  <Card className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="border-b bg-muted/40 p-4">
                        <p className="text-sm font-medium text-foreground">
                          {t('PATTERNS.PARTIALS.RESULT')}
                        </p>
                      </div>

                      <div className="flex flex-col items-center p-4">
                        <div
                          className="group relative mb-4 max-w-full cursor-zoom-in"
                          onClick={() => setIsSeamlessResultViewerOpen(true)}
                        >
                          <img
                            src={`data:image/png;base64,${seamlessResult}`}
                            alt="Seamless Texture"
                            className="block max-w-full"
                          />

                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-foreground/30 opacity-0 transition-opacity group-hover:opacity-100">
                            <ZoomIn className="size-12 text-white" />
                          </div>
                        </div>

                        <Button variant="outline" onClick={handleDownload}>
                          <Download data-icon="inline-start" />
                          Download Texture
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex h-[calc(100vh-220px)] max-h-[85vh] flex-col items-center justify-center rounded-xl border bg-card p-12 text-center">
                <Wand2 className="mb-6 size-16 text-muted-foreground" />
                <h2 className="font-heading mb-3 text-xl font-medium tracking-tight text-foreground">
                  {t('PATTERNS.PARTIALS.SELECT_PATTERN_TITLE')}
                </h2>
                <p className="max-w-[500px] text-sm text-muted-foreground">
                  {t('PATTERNS.PARTIALS.SELECT_PATTERN_DESCRIPTION')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isSeamlessResultViewerOpen &&
        <SeamlessResultDialog
          isOpen={isSeamlessResultViewerOpen}
          setIsOpen={(value) => setIsSeamlessResultViewerOpen(value)}
          seamlessResult={seamlessResult}
        />
      }
      </FeatureGate>
        </PageContainer>
    );
};
